
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Check } from "lucide-react";

interface CustomerFormData {
  name: string;
  email: string;
  phone: string;
  notes: string;
  smsConsent: boolean;
  marketingConsent: boolean;
}

interface AddCustomerFormProps {
  onSubmit: (customer: CustomerFormData) => void;
}

export function AddCustomerForm({ onSubmit }: AddCustomerFormProps) {
  const [formData, setFormData] = useState<CustomerFormData>({
    name: "",
    email: "",
    phone: "",
    notes: "",
    smsConsent: false,
    marketingConsent: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (field: keyof CustomerFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Customer name is required");
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      onSubmit(formData);
      setIsSubmitting(false);
    }, 1000);
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          placeholder="John Smith"
          required
        />
      </div>
      
      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          placeholder="john@example.com"
        />
      </div>
      
      <div className="space-y-1.5">
        <Label htmlFor="phone">Phone</Label>
        <Input
          id="phone"
          value={formData.phone}
          onChange={(e) => handleChange("phone", e.target.value)}
          placeholder="(555) 123-4567"
        />
      </div>

      {/* Consent section */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-start space-x-2">
          <Checkbox
            id="smsConsent"
            checked={formData.smsConsent}
            onCheckedChange={(v) => setFormData((p) => ({ ...p, smsConsent: !!v }))}
          />
          <div className="space-y-1">
            <Label htmlFor="smsConsent" className="text-sm">I agree to receive appointment SMS reminders.</Label>
            <p className="text-xs text-muted-foreground">Standard messaging rates may apply. You can opt out anytime.</p>
          </div>
        </div>
        <div className="flex items-start space-x-2">
          <Checkbox
            id="marketingConsent"
            checked={formData.marketingConsent}
            onCheckedChange={(v) => setFormData((p) => ({ ...p, marketingConsent: !!v }))}
          />
          <div className="space-y-1">
            <Label htmlFor="marketingConsent" className="text-sm">I agree to receive occasional marketing messages.</Label>
            <p className="text-xs text-muted-foreground">We’ll only send relevant offers. Opt out anytime.</p>
          </div>
        </div>
      </div>
      
      <div className="space-y-1.5">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => handleChange("notes", e.target.value)}
          placeholder="Add any notes about this customer..."
          rows={2}
        />
      </div>
      
      <Button 
        type="submit" 
        className="w-full bg-figgy-500 hover:bg-figgy-600"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <div className="animate-spin h-4 w-4 mr-2 border-b-2 rounded-full"></div>
            Adding Customer...
          </>
        ) : (
          <>
            <Check className="mr-2 h-4 w-4" />
            Add Customer
          </>
        )}
      </Button>
    </form>
  );
}
